import { contrastRatio, parseHex } from "./contrast.ts";

/** Independent colour tables. No density, geometry, motion or workspace state belongs here. */
export const colorSchemes = {
  midnight: {
    label: "Midnight",
    mode: "dark",
    surfaces: ["#090E18", "#0D1524", "#142033", "#203047"],
    accent: "#79AFFF",
  },
  graphite: {
    label: "Graphite",
    mode: "dark",
    surfaces: ["#101112", "#181A1C", "#222528", "#303438"],
    accent: "#A8B9D0",
  },
  oled: {
    label: "OLED",
    mode: "dark",
    surfaces: ["#000000", "#000000", "#101010", "#222222"],
    accent: "#74BBFF",
  },
  nord: {
    label: "Nord Frost",
    mode: "dark",
    surfaces: ["#202833", "#252F3D", "#303D4D", "#3A495B"],
    accent: "#A3DEEB",
  },
  dusk: {
    label: "Dusk",
    mode: "dark",
    surfaces: ["#191314", "#231B1D", "#302428", "#403137"],
    accent: "#E6B59C",
  },
  highContrast: {
    label: "High Contrast",
    mode: "dark",
    surfaces: ["#000000", "#000000", "#080808", "#161616"],
    accent: "#FFFF00",
  },
  paper: {
    label: "Paper",
    mode: "light",
    surfaces: ["#EEEDE8", "#FAF9F4", "#F1F0EA", "#E2E1DA"],
    accent: "#2451A1",
  },
  daylight: {
    label: "Daylight",
    mode: "light",
    surfaces: ["#F3F7FC", "#FFFFFF", "#EDF3FA", "#DEE8F4"],
    accent: "#154FB0",
  },
} as const;

/**
 * Mood themes (UI-REVAMP §11, §16; owner 2026-09-30 "fun, unique, cool, sleek, sexy, far out, pick
 * by mood"). Each is an ordinary colour scheme over the same token set as the base schemes, plus
 * optional palette overrides; the chrome-only "flair" layer lives in `moods.css`, keyed off
 * `data-mood` (never over chart or DOM canvases). Every override is a declared colour that already
 * passes the contrast gate on its own (schemes.test.ts checks it was not auto-lifted), so the theme
 * looks as designed. Inspired-by palettes use colours only: no names, logos, marks or likenesses.
 *
 * `sides` themes the Green / Red side palette only; Blue / Red and Colour-blind safe stay the
 * standard pairs, so the side-palette axis remains the user's. `suggestedSides` is a hint shown in
 * Settings, never applied automatically.
 */
export const moodSchemes = {
  motorCity: {
    label: "Motor City",
    mood: "Game day, confident",
    flair: "Brushed-silver hairlines, pinstripes in empty areas, blue focus glow",
    mode: "dark",
    surfaces: ["#0B0F14", "#0E141B", "#141C25", "#1C2733"],
    accent: "#4AA6E6",
    text: "#F4F6F8",
    secondary: "#B0B7BC",
    muted: "#939CA3",
    outline: "#26313C",
    line: "#46525E",
    grid: "#1D2731",
    warn: "#FFC76E",
    danger: "#FF8290",
    suggestedSides: "blueRed",
  },
  neonPunk: {
    label: "Neon Punk",
    mood: "Aggressive, high energy",
    flair: "Glowing 1 px borders, hazard-stripe panel headers",
    mode: "dark",
    surfaces: ["#0A0A0F", "#0E0E15", "#15151F", "#1E1E2B"],
    accent: "#00F0FF",
    text: "#F7F7FB",
    secondary: "#CFCFE0",
    muted: "#A3A3BC",
    outline: "#262636",
    line: "#4A4A66",
    grid: "#1F1F2E",
    warn: "#FFE600",
    danger: "#FF5C8A",
    sides: { buy: "#B6FF00", sell: "#FF2BD6", buyBg: "#3B5C00", sellBg: "#8A0F72" },
  },
  irie: {
    label: "Irie",
    mood: "Calm, patient, no FOMO",
    flair: "Soft red-gold-green stripe on the top bar, slow breathing glow",
    mode: "dark",
    surfaces: ["#0F1A12", "#122016", "#17281B", "#1D3122"],
    accent: "#E8B923",
    text: "#EFE6CF",
    secondary: "#C9C0A6",
    muted: "#A79F86",
    outline: "#22362A",
    line: "#3C5242",
    grid: "#1E3123",
    warn: "#EBC24A",
    danger: "#EE9480",
    sides: { buy: "#5DBB69", sell: "#E5866B", buyBg: "#2E6B37", sellBg: "#8E3A26" },
  },
  wildcard: {
    label: "Wildcard",
    mood: "Chaotic, theatrical",
    flair: "Card-suit dividers, skewed headings, acid-green rules",
    mode: "dark",
    surfaces: ["#14081C", "#1A0B25", "#230F32", "#2D143F"],
    accent: "#C690FF",
    text: "#F4F1EA",
    secondary: "#D6CCE0",
    muted: "#B2A3C2",
    outline: "#34184A",
    line: "#5A3A72",
    grid: "#2D1742",
    warn: "#E8D44D",
    danger: "#FF6B75",
    sides: { buy: "#7CFC00", sell: "#FF4D5E", buyBg: "#2F6B00", sellBg: "#A10F1C" },
  },
  tide: {
    label: "Tide",
    mood: "Calm, spacious",
    flair: "Slow wave line on the top bar, caustic shimmer in empty areas",
    mode: "dark",
    surfaces: ["#06202B", "#082632", "#0C2F3D", "#113A4A"],
    accent: "#5CD0C8",
    text: "#F1E9DA",
    secondary: "#C9D8D3",
    muted: "#9DB6B5",
    outline: "#14404F",
    line: "#2E5E6E",
    grid: "#11384A",
    warn: "#F2B45C",
    danger: "#FF9A85",
    sides: { buy: "#4CC6A2", sell: "#F28E76", buyBg: "#1C6B57", sellBg: "#9A3F2C" },
  },
  evergreen: {
    label: "Evergreen",
    mood: "Grounded, patient",
    flair: "Pine-ridge silhouette in empty areas, wood-grain hairline under headers",
    mode: "dark",
    surfaces: ["#0D1A14", "#10201A", "#152820", "#1C3229"],
    accent: "#9BCB7E",
    text: "#E4E9D8",
    secondary: "#C3CCB5",
    muted: "#A0AB94",
    outline: "#21382D",
    line: "#46604F",
    grid: "#1D3127",
    warn: "#D9A441",
    danger: "#F08378",
    sides: { buy: "#56C98F", sell: "#EC7468", buyBg: "#23683F", sellBg: "#8E2F27" },
  },
  obsidianGold: {
    label: "Obsidian & Gold",
    mood: "Sleek, luxurious",
    flair: "Thin gold rules, serif headings, soft specular buttons",
    mode: "dark",
    surfaces: ["#08080A", "#0C0C0F", "#131316", "#1C1C21"],
    accent: "#D4AF37",
    text: "#F6F1E3",
    secondary: "#D6CFBD",
    muted: "#ABA493",
    outline: "#24221E",
    line: "#4A4436",
    grid: "#1F1F24",
    warn: "#FFA552",
    danger: "#FF7B7B",
  },
  synthwave: {
    label: "Synthwave '84",
    mood: "Nostalgic, fun",
    flair: "Sunset gradient header, neon grid horizon in empty areas",
    mode: "dark",
    surfaces: ["#120A24", "#160C2C", "#1E1238", "#291846"],
    accent: "#FF4FD8",
    text: "#FDF2FF",
    secondary: "#DCC8EE",
    muted: "#B6A0CE",
    outline: "#2E1C4C",
    line: "#5A3A86",
    grid: "#281A46",
    warn: "#FFB454",
    danger: "#FF6B81",
    sides: { buy: "#2DE2B4", sell: "#FF5C7A", buyBg: "#0E6B55", sellBg: "#A01D3D" },
  },
} as const satisfies Record<string, MoodScheme>;

/** A mood scheme: base fields plus the palette overrides the base schemes take from defaults. */
type MoodScheme = {
  label: string;
  mood: string;
  flair: string;
  mode: "dark" | "light";
  surfaces: readonly [string, string, string, string];
  accent: string;
  text: string;
  secondary: string;
  muted: string;
  outline: string;
  line: string;
  grid: string;
  warn: string;
  danger: string;
  sides?: { buy: string; sell: string; buyBg: string; sellBg: string };
  suggestedSides?: SchemeSidePalette;
};

export type ColorSchemeId = keyof typeof colorSchemes;
export type MoodSchemeId = keyof typeof moodSchemes;
export type AnySchemeId = ColorSchemeId | MoodSchemeId;
export type SchemeSidePalette = "greenRed" | "blueRed" | "colorBlindSafe";

/** Base schemes and moods in picker order (Settings groups them; ids never collide). */
export const allSchemes: Record<
  AnySchemeId,
  {
    label: string;
    mode: "dark" | "light";
    accent: string;
    surfaces: readonly [string, string, string, string];
  }
> = {
  ...colorSchemes,
  ...moodSchemes,
};

export function isMoodScheme(id: string): id is MoodSchemeId {
  return Object.hasOwn(moodSchemes, id);
}

/** Retains the hue while moving towards black/white until every panel passes normal-text AA. */
function readable(color: string, surfaces: readonly string[], light: boolean): string {
  const rgb = parseHex(color);
  const target = light ? 0 : 255;
  for (let step = 0; step <= 100; step++) {
    const mixed =
      "#" +
      rgb
        .map((c) =>
          Math.round(c + ((target - c) * step) / 100)
            .toString(16)
            .padStart(2, "0"),
        )
        .join("");
    if (surfaces.every((bg) => contrastRatio(mixed, bg) >= 4.5)) return mixed;
  }
  return light ? "#000000" : "#FFFFFF";
}

/** Scheme plus semantic side choice; style is deliberately not an input. */
export function schemeTokens(
  id: AnySchemeId,
  palette: SchemeSidePalette = "greenRed",
  customAccent: string | null = null,
): Record<string, string> {
  const s = allSchemes[id];
  // Base schemes have no overrides: every `m?.x ?? default` below reads the original default.
  const m: MoodScheme | null = isMoodScheme(id) ? moodSchemes[id] : null;
  const light = s.mode === "light";
  const surfaces = s.surfaces;
  const fg = (color: string) => readable(color, surfaces, light);
  const [page, panel, raised, hover] = surfaces;
  const themedSides = palette === "greenRed" ? m?.sides : undefined;
  const buy = fg(themedSides?.buy ?? (palette === "greenRed" ? "#40D494" : "#63B9FF"));
  const sell = fg(themedSides?.sell ?? (palette === "colorBlindSafe" ? "#EFAD55" : "#FF7987"));
  const buyBg = themedSides?.buyBg ?? (palette === "greenRed" ? "#09603D" : "#175692");
  const sellBg = themedSides?.sellBg ?? (palette === "colorBlindSafe" ? "#864500" : "#9E263C");
  const accent = fg(customAccent && /^#[0-9a-f]{6}$/i.test(customAccent) ? customAccent : s.accent);
  const text = m ? fg(m.text) : light ? "#111820" : "#F6F8FC";
  const secondary = fg(m?.secondary ?? (light ? "#414A54" : "#C2CDD9"));
  const muted = fg(m?.muted ?? (light ? "#5D6570" : "#A3B0C2"));
  const warn = fg(m?.warn ?? (light ? "#805000" : "#FFC76E"));
  const danger = fg(m?.danger ?? (light ? "#9E2034" : "#FF8290"));
  const line = m?.line ?? (light ? "#8795A5" : "#65768C");
  return {
    "bg-0": page,
    "bg-1": panel,
    "bg-2": raised,
    "bg-3": hover,
    outline: m?.outline ?? (light ? "#B6BBC0" : "#506075"),
    border: line,
    "text-1": text,
    "text-2-strong": secondary,
    "text-2": secondary,
    "text-3": muted,
    "text-4": muted,
    accent,
    "on-accent": contrastRatio(accent, "#000000") >= contrastRatio(accent, "#FFFFFF") ? "#000000" : "#FFFFFF",
    link: accent,
    buy,
    "buy-text": buy,
    "buy-bg": buyBg,
    "buy-weak": `color-mix(in srgb, ${buy} 16%, transparent)`,
    sell,
    "sell-text": sell,
    "sell-bg": sellBg,
    "sell-bright": sell,
    "sell-weak": `color-mix(in srgb, ${sell} 16%, transparent)`,
    "on-side": "#FFFFFF",
    warn,
    danger,
    // Danger exits: the same amber family in every scheme, never a side colour (schemes.test.ts).
    "danger-exit": fg(light ? "#755800" : "#F2C230"),
    "danger-exit-bg": light ? "#E6B800" : "#F2C230",
    "on-danger-exit": "#111111",
    info: accent,
    last: text,
    "chart-grid": m?.grid ?? (light ? "#CAD1D9" : "#4A586A"),
    "chart-text": muted,
    "net-connected": buy,
    "net-connecting": muted,
    "net-unstable": warn,
    "net-disconnected": danger,
    "risk-ok": buy,
    "risk-warn": warn,
    "risk-danger": danger,
    "chart-bg": panel,
    line,
  };
}
